import React, { useState } from 'react';
import { Box, Chip, Typography, Tooltip } from '@mui/material';
import { AccountTree } from '@mui/icons-material';

export type GroupChipsBarProps = {
  groupFields: string[];
  groupCaptions: Map<string, string>;
  onReorder: (fromIdx: number, toIdx: number) => void;
  onRemove: (field: string) => void;
  onAddField?: (field: string) => void;
};

export const GroupChipsBar: React.FC<GroupChipsBarProps> = ({
  groupFields,
  groupCaptions,
  onReorder,
  onRemove,
  onAddField,
}) => {
  const [dragFromIdx, setDragFromIdx] = useState<number>(-1);
  const [dragOverIdx, setDragOverIdx] = useState<number>(-1);
  const [externalDragOver, setExternalDragOver] = useState(false);

  // ── Chip drag handlers ────────────────────────────────────────────────────

  const handleChipDragStart = (idx: number) => (e: React.DragEvent) => {
    setDragFromIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleChipDragEnd = () => {
    setDragFromIdx(-1);
    setDragOverIdx(-1);
    setExternalDragOver(false);
  };

  const handleChipDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragFromIdx !== -1) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverIdx(idx);
    } else {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleChipDrop = (toIdx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (dragFromIdx !== -1) {
      if (dragFromIdx !== toIdx) {
        onReorder(dragFromIdx, toIdx);
      }
    } else {
      handleBarDrop(e);
    }
    setDragFromIdx(-1);
    setDragOverIdx(-1);
    setExternalDragOver(false);
  };

  // ── Bar-level handlers for external (column-header) drags ─────────────────

  const handleBarDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragFromIdx !== -1 ? 'move' : 'copy';
    if (!externalDragOver) {
      setExternalDragOver(true);
    }
  };

  const handleBarDragLeave = (e: React.DragEvent) => {
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setExternalDragOver(false);
  };

  const handleBarDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setExternalDragOver(false);
    setDragOverIdx(-1);

    if (dragFromIdx === -1) {
      const field =
        e.dataTransfer.getData('text/group-field') ||
        e.dataTransfer.getData('text/column-id') ||
        e.dataTransfer.getData('text/plain');

      if (field && onAddField) {
        onAddField(field);
      }
    }
    setDragFromIdx(-1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  const isEmpty = groupFields.length === 0;

  return (
    <Box
      onDragOver={handleBarDragOver}
      onDragLeave={handleBarDragLeave}
      onDrop={handleBarDrop}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        minHeight: 36,
        overflow: 'hidden',
        flexWrap: 'wrap',
        borderBottom: '1px solid',
        borderColor: externalDragOver ? 'primary.main' : 'divider',
        bgcolor: externalDragOver ? 'action.hover' : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      {/* Label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
          flexShrink: 0,
          pointerEvents: 'none',
        }}
      >
        <AccountTree sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', userSelect: 'none' }}>
          Grouped by:
        </Typography>
      </Box>

      {/* Group chips */}
      {groupFields.map((field, idx) => {
        const caption = groupCaptions.get(field) ?? field;
        const isDropTarget = dragOverIdx === idx && dragFromIdx !== idx;

        return (
          <Tooltip
            key={field}
            title={`Level ${idx + 1} grouping — drag to reorder`}
            placement="top"
          >
            <Chip
              label={caption}
              size="small"
              onDelete={() => onRemove(field)}
              draggable
              onDragStart={handleChipDragStart(idx)}
              onDragEnd={handleChipDragEnd}
              onDragOver={handleChipDragOver(idx)}
              onDrop={handleChipDrop(idx)}
              sx={{
                cursor: 'grab',
                fontWeight: 500,
                '&:active': { cursor: 'grabbing' },
                outline: isDropTarget ? '2px solid' : 'none',
                outlineColor: 'primary.main',
                outlineOffset: '1px',
                transform: isDropTarget ? 'scale(1.04)' : 'none',
                transition: 'outline 0.1s, transform 0.1s',
              }}
            />
          </Tooltip>
        );
      })}

      {/* Hint when the bar is empty */}
      {isEmpty && onAddField && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            fontStyle: 'italic',
            border: '1px dashed',
            borderColor: externalDragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          Drag a column header here to group
        </Typography>
      )}
    </Box>
  );
};
